/**
 * MediaCompressor.js
 * Client-side image compression + video trimming utilities
 */

/**
 * Compress an image file using canvas.
 * @param {File} file - Original image file
 * @param {number} maxDimension - Max width/height in px (default 1280)
 * @param {number} quality - JPEG quality 0-1 (default 0.82)
 * @returns {Promise<Blob>}
 */
export function compressImage(file, maxDimension = 1280, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas toBlob failed'));
        },
        outputType,
        outputType === 'image/jpeg' ? quality : undefined
      );
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Trim a video between startSec and endSec using MediaRecorder.
 * Falls back gracefully if MediaRecorder is not supported.
 * @param {File|Blob} file - Input video file
 * @param {number} startSec - Start time in seconds
 * @param {number} endSec - End time in seconds
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<Blob>}
 */
export function trimVideo(file, startSec, endSec, onProgress) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      const duration = endSec - startSec;
      if (duration <= 0) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Invalid trim range'));
        return;
      }

      // Check MediaRecorder support
      const mimeTypes = [
        'video/webm;codecs=vp9',
        'video/webm;codecs=vp8',
        'video/webm',
        'video/mp4',
      ];
      let mimeType = '';
      for (const mt of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mt)) {
          mimeType = mt;
          break;
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 360;
      const ctx = canvas.getContext('2d');

      const canvasStream = canvas.captureStream(30);

      // Add audio if available
      let recorderStream = canvasStream;
      try {
        if (video.captureStream) {
          const videoStream = video.captureStream();
          const audioTracks = videoStream.getAudioTracks();
          if (audioTracks.length > 0) {
            audioTracks.forEach(track => canvasStream.addTrack(track));
          }
        }
      } catch (_) {}

      const chunks = [];
      const recorderOptions = mimeType ? { mimeType, videoBitsPerSecond: 2000000 } : {};
      let recorder;
      try {
        recorder = new MediaRecorder(canvasStream, recorderOptions);
      } catch (e) {
        URL.revokeObjectURL(objectUrl);
        reject(e);
        return;
      }

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      recorder.onstop = () => {
        URL.revokeObjectURL(objectUrl);
        const blob = new Blob(chunks, { type: mimeType || 'video/webm' });
        resolve(blob);
      };

      recorder.onerror = (e) => {
        URL.revokeObjectURL(objectUrl);
        reject(e.error || new Error('MediaRecorder error'));
      };

      video.currentTime = startSec;

      video.onseeked = () => {
        video.onseeked = null;
        recorder.start(100);
        video.play();

        const drawFrame = () => {
          if (video.currentTime >= endSec) {
            video.pause();
            recorder.stop();
            return;
          }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const progress = Math.min(100, Math.round(((video.currentTime - startSec) / duration) * 100));
          onProgress && onProgress(progress);
          requestAnimationFrame(drawFrame);
        };
        drawFrame();
      };
    };

    video.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Video load error'));
    };
  });
}

/**
 * Auto-process a file before upload:
 * - Images: compress
 * - Videos: if trim range given, trim; then return
 * @param {File} file
 * @param {object} options
 * @param {number} [options.startSec]
 * @param {number} [options.endSec]
 * @param {function} [options.onProgress]
 * @returns {Promise<{blob: Blob, name: string, type: string}>}
 */
export async function processFileForUpload(file, options = {}) {
  const { startSec, endSec, onProgress } = options;
  const isImage = file.type.startsWith('image/');
  const isVideo = file.type.startsWith('video/');

  if (isImage) {
    const compressed = await compressImage(file);
    return { blob: compressed, name: file.name, type: file.type };
  }

  if (isVideo && startSec !== undefined && endSec !== undefined && endSec > startSec) {
    const trimmed = await trimVideo(file, startSec, endSec, onProgress);
    const ext = trimmed.type.includes('mp4') ? '.mp4' : '.webm';
    const baseName = file.name.replace(/\.[^/.]+$/, '');
    return { blob: trimmed, name: `${baseName}_trimmed${ext}`, type: trimmed.type };
  }

  // No processing needed
  return { blob: file, name: file.name, type: file.type };
}
